import React, { use, useState, useEffect, useCallback } from "react";
import {
  Typography,
  Box,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Drawer,
  Divider,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
} from "@mui/material";

// Icons
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FilterListIcon from "@mui/icons-material/FilterList";
import { listAllAgentRuns } from "../../api/agentApi.js";
import { useFeatureFlags } from "../../context/FeatureFlagContext.tsx";
import AgentRunDetailsContent from "./AgentRunDetailsContent.tsx";
import StatusChip from "./StatusChip.tsx";
import { Artifact } from "../../types.ts";
import { downloadArtifactFile } from "../../api/agentApi.js";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";

interface AgentRun {
  id: number;
  agent_id: string | number;
  agent_version: number;
  run_number: number;
  runNumber?: number; // for legacy compatibility
  date: string;
  state: "Completed" | "Running" | "Failed";
  status?: string; // API uses 'status' field
  prompt: string;
  agent_prompt?: string; // API uses 'agent_prompt' field
  response: string;
  artifacts: Artifact[];
  artifact_count?: number; // NEW: number of artifacts from API
}

const useArtifactsLogic = (selectedRun: AgentRun | null) => {
  const handleDownload = async (event: React.MouseEvent, file: Artifact) => {
    event.stopPropagation();
    try {
      // Use correct run context for download
      const agentId = selectedRun?.agent_id ?? selectedRun?.id;
      const agentVersion =
        selectedRun?.agent_version ?? selectedRun?.agent_version;
      const runNumber = selectedRun?.run_number ?? selectedRun?.runNumber;
      const response = await downloadArtifactFile(
        agentId,
        agentVersion,
        runNumber,
        file.filename
      );
      if (!response || !response.ok)
        throw new Error(`Failed to fetch: ${response?.statusText}`);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", file.filename);
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
      alert(`Download failed for ${file.filename}.`);
    }
  };
  return { handleDownload };
};
interface Props {
  agentId: any;
  agentVersion: number;
  refreshToken?: number;
}
const AgentRuns: React.FC<Props> = ({ agentId, agentVersion, refreshToken }) => {
  const [selectedRun, setSelectedRun] = useState<AgentRun | null>(null);
  const { handleDownload } = useArtifactsLogic(selectedRun);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [currentVersion, setCurrentVersion] = useState<number | null>(null);
  const [agentRuns, setAgentRuns] = useState<any>(undefined);
  const [totalRuns, setTotalRuns] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [versionFilter, setVersionFilter] = useState<number | "all">("all");
  const [availableVersions, setAvailableVersions] = useState<number[]>([]);
  const { isEnabled } = useFeatureFlags();
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch all runs for this agent (optionally filtered by version)
  const fetchAllAgentRuns = useCallback(async () => {
    try {
      const result =
        versionFilter === "all"
          ? await (listAllAgentRuns as any)(agentId, {
              page: page + 1, // API uses 1-based pages
              page_size: rowsPerPage,
            })
          : await (listAllAgentRuns as any)(agentId, {
              page: page + 1, // API uses 1-based pages
              page_size: rowsPerPage,
              version: versionFilter,
            });
      setAgentRuns(result?.runs);
      setTotalRuns(result?.total || 0);

      // Extract unique versions from runs for filter dropdown (only when showing all)
      if (result?.runs && versionFilter === "all") {
        const versionSet = new Set<number>();
        result.runs.forEach((run: any) => versionSet.add(run.agent_version));
        const versions = Array.from(versionSet).sort((a, b) => b - a);
        setAvailableVersions(versions);
      }
    } catch (err) {
      console.error("Failed to fetch agent runs:", err);
    }
  }, [agentId, versionFilter, page, rowsPerPage]);
  
  useEffect(() => {
    if (agentId) fetchAllAgentRuns();
  }, [agentId, fetchAllAgentRuns, refreshToken]);

  // Filter runs by search query
  const filteredRuns = agentRuns?.filter((run: AgentRun) => {
    const query = searchQuery?.toLowerCase();
    if (!query) return true;
    const promptText = run.agent_prompt || run.prompt || "";
    return promptText.toLowerCase().includes(query);
  });

  // Poll for updates based on polling toggle
  useEffect(() => {
    if (!isEnabled("enablePolling") || !agentId) return;

    const hasActiveRuns = agentRuns?.some(
      (run: AgentRun) => (run.status?.toLowerCase() === "processing" || run.status?.toLowerCase() === "queued")
    );
    if (!hasActiveRuns) return;

    const pollIntervalMs = 2000;
    const pollInterval = setInterval(async () => {
      try {
        const result =
          versionFilter === "all"
            ? await (listAllAgentRuns as any)(agentId)
            : await (listAllAgentRuns as any)(agentId, {
                version: versionFilter,
              });
        setAgentRuns(result?.runs);
      } catch (err) {
        console.error("Failed to poll agent runs:", err);
      }
    }, pollIntervalMs);

    return () => clearInterval(pollInterval);
  }, [agentId, agentRuns, versionFilter, isEnabled]);

  const handleViewDetailsClick = (run: AgentRun) => {
    setSelectedRun(run);
    setDrawerOpen(true);
    setActiveTabIndex(0);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedRun(null);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
  };

  const getPromptSnippet = (prompt: string) => {
    return prompt.length > 50 ? prompt.substring(0, 47) + "..." : prompt;
  };

  const parsePrompt = (input: string): string => {
    try {
      const trimmed = input.trim();
      const parsed = JSON.parse(trimmed);
      return parsed.input || "";
    } catch (error) {
      console.error("Failed to parse JSON:", error);
      return input; // Return original if parsing fails
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        mb: 3,
      }}
    >
      <Paper sx={{ p: 2, mb: 2, mt: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
          }}
        >
          {/* Search */}
          <Box sx={{ flexGrow: 1, minWidth: 300 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search runs by prompt snippet..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchQuery("")}
                      edge="end"
                      aria-label="clear search"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          {/* Version Filter */}
          <Box sx={{ minWidth: "30%" }}>
            {/* <FilterListIcon sx={{ color: "text.secondary" }} /> */}
            <FormControl size="small" sx={{ minWidth: "100%" }}>
              <InputLabel id="version-filter-label">
                Filter by Version
              </InputLabel>
              <Select
                labelId="version-filter-label"
                id="version-filter"
                value={versionFilter}
                label="Filter by Version"
                onChange={(e) =>
                  setVersionFilter(e.target.value as number | "all")
                }
              >
                <MenuItem value="all">All Versions</MenuItem>
                {availableVersions.map((v) => (
                  <MenuItem key={v} value={v}>
                    Version {v}
                    {v === agentVersion && (
                      <Chip
                        label="Current"
                        size="small"
                        color="primary"
                        sx={{ ml: 1, height: 20 }}
                      />
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {versionFilter !== "all" && (
              <Typography variant="body2" color="text.secondary">
                Showing runs for version {versionFilter}
              </Typography>
            )}
          </Box>
          {/* Refresh Button */}
          <Tooltip title="Refresh runs">
            <IconButton onClick={fetchAllAgentRuns} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      <TableContainer
        component={Paper}
        sx={{ flexGrow: 1, maxHeight: "calc(100vh - 200px)" }}
      >
        <Table stickyHeader aria-label="agent run master table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: 600, py: 0.5 }}>
                Prompt Snippet
              </TableCell>
              <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: 600, py: 0.5 }}>
                Status
              </TableCell>
              <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: 600, py: 0.5 }}>
                Version #
              </TableCell>
              <TableCell
                align="center"
                sx={{ bgcolor: "#f5f5f5", fontWeight: 600, py: 0.5 }}
              >
                Artifacts
              </TableCell>
              <TableCell
                align="center"
                sx={{ bgcolor: "#f5f5f5", fontWeight: 600, py: 0.5 }}
              >
                Details
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRuns &&
              filteredRuns.map((run: any) => (
                <TableRow
                  key={run.id}
                  hover
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    cursor: "pointer",
                  }}
                >
                  <TableCell>
                    {getPromptSnippet(parsePrompt(run.agent_prompt))}
                  </TableCell>
                  <TableCell>
                    <StatusChip status={run.status} />
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {run.agent_version +
                      (run.agent_version === agentVersion ? " (Current)" : "")}
                  </TableCell>
                  <TableCell align="center">{run.artifact_count}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={() => handleViewDetailsClick(run)}
                      size="small"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={searchQuery ? filteredRuns?.length || 0 : totalRuns}
          page={searchQuery ? 0 : page}
          onPageChange={handleChangePage}
          rowsPerPage={searchQuery ? filteredRuns?.length || rowsPerPage : rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelDisplayedRows={
            searchQuery
              ? ({ from, to, count }) =>
                  `${from}-${to} of ${count} (filtered from ${totalRuns} total)`
              : undefined
          }
        />
      </TableContainer>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{ sx: { width: { xs: "100%", sm: "90", md: "75%" } } }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 3,
          }}
        >
          <Typography variant="h5">
            Run Details: #{selectedRun?.runNumber}
          </Typography>
          <IconButton onClick={handleCloseDrawer}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        {selectedRun && (
          <AgentRunDetailsContent
            run={selectedRun}
            onClose={() => setDrawerOpen(false)}
            activeTabIndex={activeTabIndex}
            onTabChange={(e, newValue) => setActiveTabIndex(newValue)}
            onDownload={handleDownload}
          />
        )}
      </Drawer>
    </Box>
  );
};

export default AgentRuns;
